import { Controller, useFormContext } from 'react-hook-form';
import { AlertCircle, X, ChevronDown, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import React from 'react';

interface FormSelectProps {
  name: string;
  options: { value: any; label: string; icon?: React.ReactNode }[];
  label?: string;
  placeholder?: string;
  rules?: any;
  getOptionStyle?: (value: any) => string;
  className?: string;
  disabled?: boolean;
  defaultValue?: any;
  onValueChange?: (value: string | string[]) => void;
  multiple?: boolean;
  maxSelections?: number;
}

export function FormSelect({
  name,
  label,
  placeholder = 'Select an option',
  options = [],
  rules,
  getOptionStyle,
  className,
  disabled,
  defaultValue,
  onValueChange,
  multiple = false,
  maxSelections,
}: FormSelectProps) {
  const { control } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue || (multiple ? [] : '')}
      render={({
        field: { onChange, value: fieldValue, ...field },
        fieldState: { error },
      }) => {
        const normalizedValue = (
          Array.isArray(fieldValue) ? fieldValue : []
        ).map((item) =>
          typeof item === 'object' && item !== null && 'value' in item
            ? item.value
            : item,
        );

        const value = multiple ? normalizedValue : fieldValue;

        const selectedOptions: any =
          options &&
          options.length &&
          options?.filter((opt) =>
            multiple ? value.includes(opt.value) : opt.value === value,
          );

        const handleSelect = (optionValue: any) => {
          let newValue: any;
          if (multiple) {
            const currentValues = value || [];
            if (currentValues.includes(optionValue)) {
              newValue = currentValues.filter((v: any) => v !== optionValue);
            } else if (!maxSelections || currentValues.length < maxSelections) {
              newValue = [...currentValues, optionValue];
            } else {
              return; // Max selections reached
            }
          } else {
            newValue = optionValue;
            setIsOpen(false);
          }
          onChange(newValue);
          if (onValueChange) onValueChange(newValue);
        };

        const removeSelection = (
          e: React.MouseEvent,
          valueToRemove: string,
        ) => {
          e.stopPropagation();
          const newValue = value.filter((v: any) => v !== valueToRemove);
          onChange(newValue);
          if (onValueChange) onValueChange(newValue);
        };

        return (
          <div className="space-y-2" ref={dropdownRef}>
            {label && (
              <label
                htmlFor={name}
                className="text-sm font-medium text-foreground"
              >
                {label}
              </label>
            )}

            <div className="relative">
              <button
                type="button"
                className={cn(
                  'cursor-pointer flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                  getOptionStyle && value ? getOptionStyle(value) : '',
                  error ? 'border-red-500 ring-1 ring-red-200' : '',
                  className,
                )}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                {...field}
              >
                <div className="flex-1 text-left min-w-0">
                  {multiple ? (
                    <div className="flex items-center gap-1 overflow-hidden">
                      {selectedOptions.length > 0 ? (
                        <>
                          {selectedOptions.length === 1 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm flex-shrink-0">
                              {selectedOptions[0]?.icon && (
                                <span className="flex-shrink-0 w-3 h-3">
                                  {selectedOptions[0].icon}
                                </span>
                              )}
                              <span className="truncate max-w-24">
                                {selectedOptions[0].label}
                              </span>
                              <button
                                type="button"
                                onClick={(e) =>
                                  removeSelection(e, selectedOptions[0].value)
                                }
                                className="flex-shrink-0 hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm flex-shrink-0">
                              {selectedOptions.length} selected
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-500 truncate">
                          {placeholder}
                        </span>
                      )}
                    </div>
                  ) : selectedOptions.length > 0 ? (
                    <div className="flex items-center gap-2">
                      {selectedOptions[0]?.icon && (
                        <span className="flex-shrink-0">
                          {selectedOptions[0].icon}
                        </span>
                      )}
                      <span className="truncate">
                        {selectedOptions[0].label}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500">{placeholder}</span>
                  )}
                </div>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 opacity-50 transition-transform',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>

              {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                  {options.map((option) => {
                    const isSelected = multiple
                      ? value.includes(option.value)
                      : value === option.value;
                    const isDisabled =
                      multiple &&
                      maxSelections &&
                      value.length >= maxSelections &&
                      !isSelected;

                    return (
                      <div
                        key={option.value}
                        className={cn(
                          'flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer hover:bg-secondary/10',
                          isSelected
                            ? 'bg-secondary text-secondary-foreground hover:bg-secondary'
                            : '',
                          isDisabled ? 'opacity-50 cursor-not-allowed' : '',
                        )}
                        onClick={() =>
                          !isDisabled && handleSelect(option.value)
                        }
                      >
                        {option.icon && (
                          <span className="flex-shrink-0">{option.icon}</span>
                        )}
                        <span className="flex-1">{option.label}</span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-foreground" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {multiple && maxSelections && (
              <p className="text-xs text-gray-500">
                {value.length}/{maxSelections} selected
              </p>
            )}

            {error && (
              <p className="text-xs font-medium text-red-500 flex items-center gap-1.5 px-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {error.message || 'This field is required'}
              </p>
            )}
          </div>
        );
      }}
    />
  );
}
